import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../apiContext';
import { ApiError } from '../api';
import PageTree from '../components/PageTree';
import WikiEditor from '../components/WikiEditor';
import type { WikiPageNode, WikiTree } from '../types';

function firstPage(root: WikiPageNode): string {
  const kids = [...(root.subPages ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return kids[0]?.path ?? '/';
}

function exists(root: WikiPageNode, path: string): boolean {
  if (root.path === path) return true;
  return (root.subPages ?? []).some(c => exists(c, path));
}

export default function WikiView() {
  const { project = '', wiki = '', '*': splat = '' } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [tree, setTree] = useState<WikiTree | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newPagePath, setNewPagePath] = useState<string | null>(null);

  const path = splat ? `/${decodeURIComponent(splat)}` : '';

  const loadTree = useCallback(() => {
    return api
      .getTree(project, wiki)
      .then(t => {
        setTree(t);
        return t;
      })
      .catch(e => {
        setError(e instanceof ApiError ? `${e.code}${e.detail ? `: ${e.detail}` : ''}` : String(e));
        return null;
      });
  }, [api, project, wiki]);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  // Land on the first page when none is in the URL.
  useEffect(() => {
    if (tree && !path) navigate(`/wiki/${encodeURIComponent(project)}/${encodeURIComponent(wiki)}${firstPage(tree.root)}`, { replace: true });
  }, [tree, path, project, wiki, navigate]);

  const select = (p: string) => {
    setNewPagePath(null);
    navigate(`/wiki/${encodeURIComponent(project)}/${encodeURIComponent(wiki)}${p}`);
  };

  const newPage = (parent: string) => {
    const title = window.prompt(`New page under ${parent === '/' ? 'the wiki root' : parent}\n\nPage title:`);
    if (!title) return;
    const clean = title.trim().replace(/[\\/#?*:<>|"]/g, '-');
    const p = `${parent === '/' ? '' : parent}/${clean}`;
    setNewPagePath(p);
    navigate(`/wiki/${encodeURIComponent(project)}/${encodeURIComponent(wiki)}${p}`);
  };

  const isNew = useMemo(() => !!tree && !!path && newPagePath === path && !exists(tree.root, path), [tree, path, newPagePath]);

  if (error) {
    return (
      <main className="wiki-view">
        <div className="notice notice-error" style={{ margin: 24 }}>
          Could not open this wiki: {error}
        </div>
      </main>
    );
  }

  return (
    <main className="wiki-view">
      <aside className="sidebar">
        <div className="sidebar-title" title={tree?.wiki.name}>
          {tree ? tree.wiki.name.replace(/\.wiki$/, '') : 'Loading…'}
          <span className="muted small"> · {project}</span>
        </div>
        {tree && <PageTree root={tree.root} selectedPath={path} onSelect={select} onNewPage={newPage} />}
      </aside>
      {tree && path ? (
        <WikiEditor
          key={path}
          project={project}
          wikiId={wiki}
          path={path}
          isNew={isNew}
          onSaved={() => {
            if (isNew) {
              setNewPagePath(null);
              void loadTree();
            }
          }}
        />
      ) : (
        <section className="editor-pane" />
      )}
    </main>
  );
}
